import * as React from 'react';
import { Badge, Card, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import axiosInstance from 'utils/axiosInterceptors';
import { RetryImage } from 'components/common/RetryImage';
import ImageModal, {
  ImageModalHandle,
  ImageModalProps
} from 'components/modals/ImageModal';
import { fetchImageBlobUrl } from 'utils/rotateImage';
import { getUnitLabel } from 'utils/unitOptions';
import { TShelves } from 'utils/types';

export type ShelfProduct = {
  productId: number;
  name: string;
  productBrand: string;
  productCategoryName: string;
  imageFileIds: string[];
  lastCountPhotoFileIds?: string[];
  quantityOnHand: number;
  stockUnitOfMeasure: number;
};

type GalleryImage = {
  googleFileId: string;
  caption: string;
};

type ShelfItemsPanelProps = {
  branchDomain: string;
  locationId: number;
  locationName: string;
  shelfCode: string;
  shelf?: TShelves;
  onHide: () => void;
};

const PAGE_SIZE = 40;

// Gallery is product catalog photos first, then last-count location photos.
const buildGallery = (item: ShelfProduct): GalleryImage[] => {
  const productPhotos = (item.imageFileIds ?? []).map(googleFileId => ({
    googleFileId,
    caption: 'Product photo'
  }));
  const locationPhotos = (item.lastCountPhotoFileIds ?? []).map(
    googleFileId => ({
      googleFileId,
      caption: 'Location photo'
    })
  );
  return [...productPhotos, ...locationPhotos];
};

const ShelfItemsPanel = ({
  branchDomain,
  locationId,
  locationName,
  shelfCode,
  shelf,
  onHide
}: ShelfItemsPanelProps) => {
  const [items, setItems] = React.useState<ShelfProduct[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [rowCount, setRowCount] = React.useState(0);
  const [selectedItem, setSelectedItem] = React.useState<ShelfProduct | null>(
    null
  );
  const [imageModalProps, setImageModalProps] = React.useState<ImageModalProps>();
  const imageModalRef = React.useRef<ImageModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const blobUrlRef = React.useRef<string | undefined>();
  const galleryRef = React.useRef<GalleryImage[]>([]);
  const galleryIndexRef = React.useRef(0);
  const openPhotoRef = React.useRef<(index: number) => Promise<void>>(
    async () => {}
  );

  const openPhoto = React.useCallback(async (index: number) => {
    const images = galleryRef.current;
    if (images.length === 0) return;
    const nextIndex = ((index % images.length) + images.length) % images.length;
    const photo = images[nextIndex];
    galleryIndexRef.current = nextIndex;
    try {
      const imageUrl = await fetchImageBlobUrl(photo.googleFileId);
      if (blobUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      blobUrlRef.current = imageUrl;
      imageModalRef.current.show();
      setImageModalProps({
        image: imageUrl,
        size: 'lg',
        googleFileId: photo.googleFileId,
        caption: photo.caption,
        imageIndex: nextIndex,
        imageCount: images.length,
        onPrevious:
          images.length > 1
            ? () => {
                void openPhotoRef.current(galleryIndexRef.current - 1);
              }
            : undefined,
        onNext:
          images.length > 1
            ? () => {
                void openPhotoRef.current(galleryIndexRef.current + 1);
              }
            : undefined
      });
    } catch (error) {
      console.error('Error fetching shelf item photo:', error);
    }
  }, []);
  openPhotoRef.current = openPhoto;

  React.useEffect(() => {
    return () => {
      if (blobUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!shelf) {
      setItems([]);
      setRowCount(0);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(
          `/api/v1/${branchDomain}/locations/${locationId}/shelves/${shelf.shelfId}/products`,
          { params: { page: 1, pageSize: PAGE_SIZE } }
        );
        if (cancelled) return;
        const rows = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.Data)
            ? data.Data
            : [];
        setItems(
          rows.map((row: ShelfProduct & { LastCountPhotoFileIds?: string[] }) => ({
            ...row,
            lastCountPhotoFileIds:
              row.lastCountPhotoFileIds ?? row.LastCountPhotoFileIds ?? []
          }))
        );
        setRowCount(data?.rowCount ?? data?.RowCount ?? rows.length);
      } catch {
        if (!cancelled) {
          setItems([]);
          setRowCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [branchDomain, locationId, shelf]);

  const openItemImages = (item: ShelfProduct) => {
    setSelectedItem(item);
    galleryRef.current = buildGallery(item);
    if (galleryRef.current.length === 0) return;
    void openPhoto(0);
  };

  return (
    <>
      <Card className="warehouse-shelf-panel">
        <Card.Header className="d-flex justify-content-between align-items-center py-3">
          <strong>
            {locationName} / {shelfCode}
          </strong>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onHide}
          />
        </Card.Header>
        <Card.Body>
          {!shelf && (
            <div className="text-body-tertiary">
              This shelf is on the map but has not been added in Locations yet.
            </div>
          )}
          {shelf && loading && (
            <div className="text-center py-6">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading items...
            </div>
          )}
          {shelf && !loading && items.length === 0 && (
            <div className="text-body-tertiary">No items on this shelf yet.</div>
          )}
          {shelf && !loading && items.length > 0 && (
            <>
              <div className="text-body-tertiary mb-3">
                {rowCount} item{rowCount === 1 ? '' : 's'}
              </div>
              <div className="d-flex flex-column gap-3">
                {items.map(item => (
                  <button
                    key={item.productId}
                    type="button"
                    className="btn btn-light text-start border d-flex gap-3 align-items-center p-3"
                    onClick={() => openItemImages(item)}
                  >
                    <div
                      className="flex-shrink-0 bg-body-secondary rounded overflow-hidden"
                      style={{ width: 72, height: 72 }}
                    >
                      {item.imageFileIds?.[0] || item.lastCountPhotoFileIds?.[0] ? (
                        <RetryImage
                          googleFileId={
                            item.imageFileIds?.[0] ||
                            item.lastCountPhotoFileIds![0]
                          }
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div className="h-100 d-flex align-items-center justify-content-center text-body-tertiary">
                          <FontAwesomeIcon icon={faImage} />
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-semibold text-truncate">{item.name}</div>
                      <div className="text-body-tertiary small text-truncate">
                        {item.productBrand}
                        {item.productCategoryName
                          ? ` · ${item.productCategoryName}`
                          : ''}
                      </div>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        <Badge bg="secondary">
                          {item.quantityOnHand}{' '}
                          {getUnitLabel(item.stockUnitOfMeasure)}
                        </Badge>
                        {(item.lastCountPhotoFileIds?.length ?? 0) > 0 && (
                          <Badge bg="info">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                            Location photo
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={Boolean(selectedItem) && galleryRef.current.length === 0}
        onHide={() => setSelectedItem(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-7">{selectedItem?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center text-body-tertiary py-4">
            <FontAwesomeIcon icon={faImage} className="fs-4 mb-2" />
            <div>No product or location photo for this item.</div>
          </div>
          {selectedItem && (
            <div className="mt-3 text-end">
              <Link
                to={`/product/${encodeURIComponent(branchDomain)}/${selectedItem.productId}`}
                className="fw-semibold"
              >
                Open product
              </Link>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <ImageModal
        ref={imageModalRef}
        image={imageModalProps?.image}
        size={imageModalProps?.size}
        googleFileId={imageModalProps?.googleFileId}
        caption={
          selectedItem
            ? `${selectedItem.name} · ${imageModalProps?.caption ?? ''}`
            : imageModalProps?.caption
        }
        imageIndex={imageModalProps?.imageIndex}
        imageCount={imageModalProps?.imageCount}
        onPrevious={imageModalProps?.onPrevious}
        onNext={imageModalProps?.onNext}
      />
    </>
  );
};

export default ShelfItemsPanel;
